import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useAdminDarkMode } from "../../contexts/AdminDarkModeContext";

/**
 * Card shell for settings pages — matches admin dark surfaces (Jobber-style) when theme or admin dark is on.
 */
export function getSettingsCardShellSx(theme) {
  const dark = theme?.palette?.mode === "dark";
  return {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    borderRadius: "8px",
    overflow: "hidden",
    border: dark ? "1px solid #2d3b4e" : "1px solid #E5E7EB",
    boxShadow: dark
      ? "0 1px 3px rgba(0, 0, 0, 0.35)"
      : "0 1px 2px rgba(15, 23, 42, 0.06)",
    background: dark ? "#1a2332" : "#FFFFFF",
  };
}

/** Light-mode defaults only — prefer {@link getSettingsCardShellSx} with `useTheme()`. */
export const settingsCardShellSx = {
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  borderRadius: "8px",
  overflow: "hidden",
  border: "1px solid #E5E7EB",
  boxShadow: "var(--shadow-sm)",
  background: "#FFFFFF",
};

/**
 * Card title strip — Poppins 600; adapts to dark mode (no white “icon halo”).
 * @param {boolean} [plainHeader] — if true, same surface as card body instead of muted strip.
 * @param {boolean} [centerIconWithTitle] — vertically centers icon with title block.
 */
export default function SettingsCardHeader({
  title,
  icon: Icon,
  subtitle,
  plainHeader = false,
  centerIconWithTitle = false,
}) {
  const theme = useTheme();
  const { darkMode: adminDark } = useAdminDarkMode();
  const isDark = theme.palette.mode === "dark" || adminDark;

  const borderColor = isDark ? "#2d3b4e" : "#E5E7EB";
  const headerBg = plainHeader
    ? isDark
      ? "#1a2332"
      : "#FFFFFF"
    : isDark
      ? "#151d2a"
      : "#F9FAFB";
  const titleColor = isDark ? "#e8eaed" : "#1F2937";
  const subtitleColor = isDark ? "#b8c5d6" : "#6b7280";
  const iconRingBg = isDark ? "rgba(96, 165, 250, 0.12)" : "#FFFFFF";
  const iconRingBorder = isDark ? "rgba(96, 165, 250, 0.35)" : "#E5E7EB";
  const iconGlyphColor = isDark ? "#90caf9" : "#4B5563";

  return (
    <Box
      component="header"
      sx={{
        backgroundColor: headerBg,
        borderBottom: `1px solid ${borderColor}`,
        px: { xs: 2, sm: 2.5 },
        py: { xs: 1.75, sm: 2 },
      }}
    >
      <Box
        display="flex"
        alignItems={centerIconWithTitle ? "center" : "flex-start"}
        gap={{ xs: 1.5, sm: 2 }}
        sx={{ minWidth: 0 }}
      >
        {Icon && (
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: iconRingBg,
              border: `1px solid ${iconRingBorder}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              ...(centerIconWithTitle ? {} : { mt: "2px" }),
            }}
          >
            <Icon
              sx={{
                fontSize: 20,
                color: iconGlyphColor,
                flexShrink: 0,
              }}
            />
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            component="h3"
            sx={{
              fontFamily: '"Poppins", sans-serif',
              fontWeight: 600,
              fontSize: { xs: "17px", sm: "18px" },
              lineHeight: 1.3,
              color: titleColor,
              m: 0,
              letterSpacing: "-0.01em",
              minWidth: 0,
              overflowWrap: "anywhere",
            }}
          >
            {title}
          </Typography>
          {subtitle != null && subtitle !== "" && (
            <Typography
              component="p"
              variant="body2"
              sx={{
                fontFamily: '"Poppins", sans-serif',
                color: subtitleColor,
                fontSize: "0.875rem",
                fontWeight: 400,
                lineHeight: 1.5,
                m: 0,
                mt: 0.75,
                maxWidth: "720px",
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}
